/**
 * @typedef {import('BMapsModel/atoms').Atom} Atom
 * @typedef {{
 *     ringOrdinal: number, atoms: Atom[], resSpec: string, centroid: [number, number, number]
 * }} Ring
 * @typedef {{
 *     ring1: Ring, ring2: Ring,
 *     distance: number, planeAngle: number, pipiType: string,
 *     normal1, normal2, normalAngle1, normalAngle2, GiFEenergy?: number,
 * }} PiPiStack
 * @typedef {{
 *     minDistance: number, maxDistanceFaceFace: number, maxDistanceEdgeFace: number,
 *     maxAngleFaceFace: number, minAngleEdgeFace: number,
 *     planarDotTolerance: number,
 *     debug: boolean,
 *     algorithm: 'bmaps' | 'prolif',
 *     drawBeforeEnergies: boolean,
 * }} PiPiParams
 */

import { ensureNormalToward } from 'BMapsSrc/util/atom_distance_utils';
import { Add3_3, AngleBetweenPoints, DegreesFromRadians } from 'BMapsSrc/math';
import {
    AtomGroup, Ring, ringDistance, getRingsPlaneAngle,
    isAromaticRing,
} from 'BMapsModel';

export const DefaultPiPiParams = {
    minDistance: 3.5,
    maxDistanceFaceFace: 4.5, // Maestro: 4.5, ProLIF 5.5
    maxDistanceEdgeFace: 5.5, // Maestro: 5.5, ProLIF 6.5
    maxAngleFaceFace: 30, // Maestro: 30, ProLIF 35
    minAngleEdgeFace: 60, // Maestro: 60, ProLIF 50
    // Prolif also enforces max normal_to_centroid angle:
    // 30 for edge-to-face and 33 for face-to-face
    planarDotTolerance: null,
    debug: false,
    drawBeforeEnergies: true, // draw the pi-pi interactions before calculating energies
    // pi-cation: max distance: 6.6, max angle 30 (vector toward cation, off the ring normal vector)
};

/**
 * Return pi-pi interactions found between two lists of atoms
 * @param {{ atoms1: Atom[], atoms2: Atom[], params: PiPiParams }} param0
 * @returns {PiPiStack[]}
 */
export function findPiPiStacksForAtoms({ atoms1, atoms2, params=DefaultPiPiParams }={}) {
    const atomGroups1 = AtomGroup.atomGroupsFromAtoms(atoms1);
    const atomGroups2 = AtomGroup.atomGroupsFromAtoms(atoms2);
    return findPiPiStacks({ atomGroups1, atomGroups2, params });
}

/**
 * Return pi-pi interactions found between two lists of AtomGroups
 * @param {{ atomGroups1: AtomGroup[], atomGroups2: AtomGroup[], params: PiPiParams }} params
 * @returns {PiPiStack[]}
 */
export function findPiPiStacks({ atomGroups1, atomGroups2, params=DefaultPiPiParams }={}) {
    const ringOptions = { planarDotTolerance: params.planarDotTolerance, debug: params.debug };
    const rings1 = Ring.combineAromaticRings(Ring.ringsForAtomGroups(atomGroups1), ringOptions);
    const rings2 = Ring.combineAromaticRings(Ring.ringsForAtomGroups(atomGroups2), ringOptions);
    const pipiStacks = [];
    for (const ring1 of rings1) {
        for (const ring2 of rings2) {
            const onePipi = annotatePiPi(ring1, ring2, params);
            if (!onePipi.pipiType) continue; // doesn't meet the specs

            pipiStacks.push(onePipi);
        }
    }
    if (params.debug) console.log('pipi', pipiStacks);
    return pipiStacks;
}

/**
 * @param {Ring} ring1
 * @param {Ring} ring2
 * @param {PiPiParams} params
 * @returns {PiPiStack?}
 */
export function annotatePiPi(ring1, ring2, params=DefaultPiPiParams) {
    const distance = ringDistance(ring1, ring2);
    const planeAngle = getRingsPlaneAngle(ring1, ring2);
    const { pipiType, rejectReason } = categorizePiPi(ring1, ring2, distance, planeAngle, params);

    const ringNormal1 = ensureNormalToward(ring1.centroid, ring1.normal, ring2.centroid);
    const ringNormal2 = ensureNormalToward(ring2.centroid, ring2.normal, ring1.centroid);
    const normalEnd1 = Add3_3(ring1.centroid, ringNormal1);
    const normalEnd2 = Add3_3(ring2.centroid, ringNormal2);
    const normalAngle1 = AngleBetweenPoints(normalEnd1, ring1.centroid, ring2.centroid);
    const normalAngle2 = AngleBetweenPoints(normalEnd2, ring2.centroid, ring1.centroid);
    return {
        // Essential properties
        ring1,
        ring2,
        distance,
        planeAngle,
        pipiType,
        rejectReason,
        // Extra properties about ring normals
        ringNormal1,
        ringNormal2,
        normalAngle1: DegreesFromRadians(normalAngle1),
        normalAngle2: DegreesFromRadians(normalAngle2),
    };
}

/**
 * Return pi-pi interaction type for two rings: face-to-face, edge-to-face, or null if out of spec
 * @param {Ring} ring1
 * @param {Ring} ring2
 * @param {number} distance
 * @param {number} planeAngle
 * @param {PiPiParams} params
 * @returns {string?} "Face-to-Face", "Edge-to-Face", or null
 */
function categorizePiPi(ring1, ring2, distance, planeAngle, params) {
    function reject(reason) {
        if (params.debug) {
            console.log(`Rejected pi-pi interaction between ${ring1} and ${ring2}: ${reason}`);
        }
        return { rejectReason: reason };
    }

    if (!isAromaticRing(ring1.atoms, params)) return reject(`ring1 (${ring1}) is not aromatic (${ring1.atoms.map((a) => a.amber).join(', ')})`);
    if (!isAromaticRing(ring2.atoms, params)) return reject(`ring2 (${ring2}) is not aromatic (${ring2.atoms.map((a) => a.amber).join(', ')})`);

    if (distance < params.minDistance) {
        return reject(`dist ${distance.toFixed(2)} < minDist ${params.minDistance}`);
    }
    const faceFace = planeAngle <= params.maxAngleFaceFace;
    const edgeFace = planeAngle >= params.minAngleEdgeFace;
    if (faceFace) {
        if (distance > params.maxDistanceFaceFace) {
            return reject(`dist ${distance.toFixed(2)} > maxDist FaceFace ${params.maxDistanceFaceFace}`);
        }
        return { pipiType: 'Face-to-Face' };
    } else if (edgeFace) {
        if (distance > params.maxDistanceEdgeFace) {
            return reject(`dist ${distance.toFixed(2)} > maxDist EdgeFace ${params.maxDistanceEdgeFace}`);
        }
        return { pipiType: 'Edge-to-Face' };
    } else {
        return reject(`plane angle ${planeAngle.toFixed(2)} < min EdgeFace ${params.minAngleEdgeFace} OR > max FaceFace ${params.maxAngleFaceFace})`);
    }
}
